import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { Emitter } from 'vscode/vscode/vs/base/common/event';
import { DisposableStore } from 'vscode/vscode/vs/base/common/lifecycle';
import { IModelService } from 'vscode/vscode/vs/editor/common/services/model';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { ITerminalService } from 'vscode/vscode/vs/workbench/contrib/terminal/browser/terminal.service';

let TerminalAccessibleBufferProvider = class TerminalAccessibleBufferProvider extends DisposableStore {
    constructor(_instance, _bufferTracker, customHelp, _modelService, configurationService, _contextKeyService, _terminalService) {
        super();
        this._instance = _instance;
        this._bufferTracker = _bufferTracker;
        this.id = "terminal" ;
        this.options = { type: "view" , language: 'terminal', id: "terminal"  };
        this.verbositySettingKey = "accessibility.verbosity.terminal" ;
        this._onDidRequestClearProvider = ( new Emitter());
        this.onDidRequestClearLastProvider = this._onDidRequestClearProvider.event;
        this.options.customHelp = customHelp;
        this.options.position = configurationService.getValue("terminal.integrated.accessibleViewPreserveCursorPosition" ) ? 'initial-bottom' : 'bottom';
        this.add(this._instance.onDisposed(() => this._onDidRequestClearProvider.fire("terminal" )));
        this.add(configurationService.onDidChangeConfiguration(e => {
            if (e.affectsConfiguration("terminal.integrated.accessibleViewPreserveCursorPosition" )) {
                this.options.position = configurationService.getValue("terminal.integrated.accessibleViewPreserveCursorPosition" ) ? 'initial-bottom' : 'bottom';
            }
        }));
        this._focusedInstance = _terminalService.activeInstance;
        this.add(_terminalService.onDidChangeActiveInstance(() => {
            if (_terminalService.activeInstance && this._focusedInstance?.instanceId !== _terminalService.activeInstance?.instanceId) {
                this._onDidRequestClearProvider.fire("terminal" );
                this._focusedInstance = _terminalService.activeInstance;
            }
        }));
    }
    onClose() {
        this._instance.focus();
    }
    provideContent() {
        this._bufferTracker.update();
        return this._bufferTracker.lines.join('\n');
    }
    getSymbols() {
        const commands = this._getCommandsWithEditorLine() ?? [];
        const symbols = [];
        for (const command of commands) {
            const label = command.command.command;
            if (label) {
                symbols.push({
                    label,
                    lineNumber: command.lineNumber
                });
            }
        }
        return symbols;
    }
    _getCommandsWithEditorLine() {
        const capability = this._instance.capabilities.get(2 );
        const commands = capability?.commands;
        const currentCommand = capability?.currentCommand;
        if (!commands?.length) {
            return;
        }
        const result = [];
        for (const command of commands) {
            const lineNumber = this._getEditorLineForCommand(command);
            if (lineNumber === undefined) {
                continue;
            }
            result.push({ command, lineNumber, exitCode: command.exitCode });
        }
        if (currentCommand) {
            const lineNumber = this._getEditorLineForCommand(currentCommand);
            if (lineNumber !== undefined) {
                result.push({ command: currentCommand, lineNumber });
            }
        }
        return result;
    }
    _getEditorLineForCommand(command) {
        let line;
        if ('marker' in command) {
            line = command.marker?.line;
        }
        else if ('commandStartMarker' in command) {
            line = command.commandStartMarker?.line;
        }
        if (line === undefined || line < 0) {
            return;
        }
        line = this._bufferTracker.bufferToEditorLineMapping.get(line);
        if (line === undefined) {
            return;
        }
        return line + 1;
    }
};
TerminalAccessibleBufferProvider = ( __decorate([
    ( __param(3, IModelService)),
    ( __param(4, IConfigurationService)),
    ( __param(5, IContextKeyService)),
    ( __param(6, ITerminalService))
], TerminalAccessibleBufferProvider));

export { TerminalAccessibleBufferProvider };
